/**
 * The template editor's focus trap, as a predicate and a piece of arithmetic.
 *
 * The audit (#16) found the modal neither took focus nor kept it: with the
 * editor open, a Tab walk ran through the top bar, the file list, the viewer HUD
 * and ~30 panel controls BEHIND the backdrop — 71 stops in all — so a keyboard
 * user could edit things they could not see. The fix is a `keydown` handler on
 * the dialog root that answers Tab itself; everything about it that can be
 * tested without a browser lives here, and `TemplateEditor` holds only the three
 * DOM lines that cannot (`querySelectorAll`, `indexOf`, `focus`).
 */

/**
 * Everything that can hold focus, as the selector a dialog root is asked for.
 * The `:not([disabled])` clauses and `[tabindex="-1"]` are what the selector can
 * say by itself; whether the match is actually on screen is `isFocusable`'s job.
 */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  // A hidden file input is not a stop: the editor's photo picker is exactly that
  // — the visible "Choose photo…" button clicks it — and Tab must reach the
  // button, never the input behind it.
  'input:not([disabled]):not([type=hidden])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * The properties `isFocusable` reads, and no more: an `HTMLElement` satisfies it,
 * and so does a plain object in a test. Deliberately not `getComputedStyle` —
 * `display: none` and a folded section both show up as "no client rects", which
 * is a fact the element can state about itself.
 */
export type FocusCandidate = {
  disabled?: boolean;
  hidden?: boolean;
  getClientRects?: () => { length: number };
  getBoundingClientRect?: () => { width: number; height: number };
};

/**
 * Whether a match of `FOCUSABLE_SELECTOR` is really a Tab stop: rendered, sized
 * and enabled. A candidate that can answer neither question is kept — the
 * selector already matched it, and dropping a stop we cannot measure would lose
 * a control, which is the worse failure of the two.
 */
export function isFocusable(el: FocusCandidate): boolean {
  if (el.disabled) return false;
  if (el.hidden) return false;
  const rects = el.getClientRects?.();
  if (rects && rects.length === 0) return false;
  const box = el.getBoundingClientRect?.();
  if (box && (box.width <= 0 || box.height <= 0)) return false;
  return true;
}

/**
 * The visible stops of a selector match, in the order it gave them — which for
 * `querySelectorAll` is document order, i.e. reading order down the dialog.
 */
export function focusableFrom<T extends FocusCandidate>(nodes: ArrayLike<T>): T[] {
  return Array.from(nodes).filter(isFocusable);
}

/**
 * Where Tab goes from the stop at `current`, in a set of `count` of them:
 * forward, or backward with Shift, wrapping at both ends. That wrap IS the trap.
 *
 * `current` is an `indexOf` result, so −1 ("focus is not on one of our stops" —
 * the backdrop, the dialog root, or a control that has just been replaced)
 * enters at the first stop going forward and the last going back. Out-of-range
 * indices are treated the same way, because the set is recomputed on every Tab
 * and a control can disappear between two presses. `−1` back means there is
 * nothing to focus and the caller should leave focus where it is.
 */
export function nextFocusIndex(current: number, count: number, shift: boolean): number {
  if (count <= 0) return -1;
  if (current < 0 || current >= count) return shift ? count - 1 : 0;
  return shift ? (current - 1 + count) % count : (current + 1) % count;
}
